import { z } from "zod";

import { formatBytes } from "@/lib/utils/format";
import {
  MAX_FEEDBACK_ATTACHMENTS,
  MAX_FEEDBACK_ATTACHMENT_BYTES,
  MAX_FEEDBACK_ATTACHMENTS_TOTAL_BYTES,
} from "@/lib/feedback/attachment-limits";

export const FEEDBACK_TYPES = ["software_issue", "feature_request"] as const;

export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  software_issue: "Software issue",
  feature_request: "Feature request",
};

export const FEEDBACK_URGENCIES = ["bug", "outage"] as const;

export type FeedbackUrgency = (typeof FEEDBACK_URGENCIES)[number];

export const FEEDBACK_URGENCY_LABELS: Record<FeedbackUrgency, string> = {
  bug: "Not working as intended",
  outage: "Outage — blocking my work",
};

export const FEEDBACK_ATTACHMENT_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

export type FeedbackAttachmentMimeType = (typeof FEEDBACK_ATTACHMENT_MIME_TYPES)[number];

export const FEEDBACK_ATTACHMENT_ACCEPT = FEEDBACK_ATTACHMENT_MIME_TYPES.join(",");

export {
  MAX_FEEDBACK_ATTACHMENTS,
  MAX_FEEDBACK_ATTACHMENT_BYTES,
  MAX_FEEDBACK_ATTACHMENTS_TOTAL_BYTES,
} from "@/lib/feedback/attachment-limits";

function claimsAllowedImageType(type: string): type is FeedbackAttachmentMimeType {
  return (FEEDBACK_ATTACHMENT_MIME_TYPES as readonly string[]).includes(type);
}

export const feedbackAttachmentSchema = z
  .instanceof(File)
  .refine((file) => claimsAllowedImageType(file.type), {
    message: "Attach PNG, JPEG, WebP, or GIF images only",
  })
  .refine((file) => file.size <= MAX_FEEDBACK_ATTACHMENT_BYTES, {
    message: `Each image must be ${formatBytes(MAX_FEEDBACK_ATTACHMENT_BYTES)} or smaller`,
  });

export const feedbackAttachmentsSchema = z.array(z.instanceof(File)).superRefine((files, ctx) => {
  const reportAgainstWholeList = (message: string) => ctx.addIssue({ code: "custom", message });

  if (files.length > MAX_FEEDBACK_ATTACHMENTS) {
    return reportAgainstWholeList(`Attach at most ${MAX_FEEDBACK_ATTACHMENTS} images`);
  }

  for (const file of files) {
    const perFile = feedbackAttachmentSchema.safeParse(file);
    if (!perFile.success) {
      return reportAgainstWholeList(perFile.error.issues[0].message);
    }
  }

  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_FEEDBACK_ATTACHMENTS_TOTAL_BYTES) {
    reportAgainstWholeList(
      `Attachments must total ${formatBytes(MAX_FEEDBACK_ATTACHMENTS_TOTAL_BYTES)} or less`
    );
  }
});

export function firstFeedbackAttachmentError(files: File[]): string | null {
  const result = feedbackAttachmentsSchema.safeParse(files);
  return result.success
    ? null
    : (result.error.issues[0]?.message ?? "Those images can't be attached");
}

export const feedbackFormSchema = z
  .object({
    type: z.enum(FEEDBACK_TYPES),
    summary: z.string().trim().min(1, "Please enter a title"),
    details: z.string().trim().optional(),
    urgency: z.enum(FEEDBACK_URGENCIES).optional(),
    attachments: feedbackAttachmentsSchema.optional(),
  })
  .refine((data) => data.type !== "software_issue" || !!data.urgency, {
    message: "Select an urgency",
    path: ["urgency"],
  });

export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

export const feedbackDiagnosticsSchema = z
  .object({
    pageUrl: z.string().optional(),
    routeParams: z.record(z.string(), z.string()).optional(),
    environment: z.string().optional(),
    release: z.string().optional(),
    appVersion: z.string().optional(),
    userAgent: z.string().optional(),
    datadogSessionId: z.string().optional(),
    datadogReplayUrl: z.string().optional(),
  })
  .partial();

export type FeedbackDiagnostics = z.infer<typeof feedbackDiagnosticsSchema>;
